// The live trip every screen reads: the pack with the family's edits folded in (domain/trip).
// Only trip edits change it, so answering a card doesn't rebuild the Map's world.
import { useMemo } from 'react'
import type { Pack } from '../../shared/pack/schema'
import { projectTrip } from '../domain/trip/project'
import { placeId } from '../map/places/registry'
import { useEvents } from './progress'

/**
 * Stages name their place by today's id: a cached pack or a synced edit may still carry an old
 * one (`mountain` for Niseko, map/places/registry PLACE_ALIASES).
 */
export function withPlaceIds<P extends Pick<Pack, 'itinerary'>>(pack: P): P {
  if (pack.itinerary.stages.every((s) => placeId(s.spot) === s.spot)) return pack
  return { ...pack, itinerary: { ...pack.itinerary, stages: pack.itinerary.stages.map((s) => ({ ...s, spot: placeId(s.spot) })) } }
}

export function useTripPack(pack: Pack | null): Pack | null {
  const events = useEvents()
  const edits = useMemo(() => events.filter((e) => e.type.startsWith('trip.')), [events])
  // Same edits (and the same sync state) → same key → the same projected object.
  const key = edits.map((e) => `${e.id}:${e.seq ?? ''}`).join(',')
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` stands for `edits`
  return useMemo(() => (pack ? withPlaceIds(projectTrip(pack, edits)) : null), [pack, key])
}
